import { useState, useEffect } from 'preact/hooks';
import { getCalendarData, getUsers, getDepartments, getLocations, getHolidays, getCalendarConnections, connectCalendar, disconnectCalendar, syncAllCalendarEvents, syncTeamCalendarEvents, setTeamSyncPreference, getWorkspaceSettings } from '../lib/api';
import { useI18n } from '../i18n/I18nContext';
import { getPrivacyAwareLeaveColor, getPrivacyAwareLeaveTypeName } from '../lib/leave-privacy';

export function CalendarPage({ user, workspace }) {
  const { t, locale } = useI18n();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewType, setViewType] = useState('month'); // 'day', 'week', 'month'
  const [calendarData, setCalendarData] = useState({ leaves: [], holidays: [] });
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // Calendar connections state
  const [calendarConnections, setCalendarConnections] = useState([]);
  const [connectingProvider, setConnectingProvider] = useState(null);
  const [connectionMessage, setConnectionMessage] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncingTeam, setSyncingTeam] = useState(false);
  const [togglingTeamSync, setTogglingTeamSync] = useState(false);
  const [calendarIntegrationEnabled, setCalendarIntegrationEnabled] = useState(true);

  // Filters
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [leaveTypeFilter, setLeaveTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Day detail modal
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    loadData();
    loadCalendarConnections();
    loadWorkspaceSettings();

    // Check URL for OAuth callback result
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('calendar_success');
    const error = urlParams.get('calendar_error');
    if (success) {
      setConnectionMessage({ type: 'success', text: success });
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (error) {
      setConnectionMessage({ type: 'error', text: error });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [currentDate, viewType]);

  async function loadWorkspaceSettings() {
    try {
      const data = await getWorkspaceSettings();
      setCalendarIntegrationEnabled(data.calendar_integration_enabled !== false);
    } catch (err) {
      console.error('Failed to load workspace settings:', err);
    }
  }

  async function loadCalendarConnections() {
    try {
      const data = await getCalendarConnections();
      setCalendarConnections(data.connections || []);
    } catch (err) {
      console.error('Failed to load calendar connections:', err);
    }
  }

  async function handleConnectCalendar(provider) {
    setConnectingProvider(provider);
    setConnectionMessage(null);
    try {
      const data = await connectCalendar(provider);
      if (data.authUrl) {
        // Redirect to OAuth provider
        window.location.href = data.authUrl;
      } else {
        setConnectionMessage({ type: 'error', text: 'Failed to get authorization URL' });
      }
    } catch (err) {
      setConnectionMessage({ type: 'error', text: err.message || 'Failed to connect calendar' });
    } finally {
      setConnectingProvider(null);
    }
  }

  async function handleDisconnectCalendar(provider) {
    if (!confirm(`Are you sure you want to disconnect your ${provider === 'google_calendar' ? 'Google' : 'Microsoft'} Calendar?`)) {
      return;
    }
    try {
      await disconnectCalendar(provider);
      setCalendarConnections(prev => prev.filter(c => c.provider !== provider));
      setConnectionMessage({ type: 'success', text: 'Calendar disconnected successfully' });
    } catch (err) {
      setConnectionMessage({ type: 'error', text: err.message || 'Failed to disconnect calendar' });
    }
  }

  async function handleSyncAll() {
    setSyncingAll(true);
    setConnectionMessage(null);
    try {
      const result = await syncAllCalendarEvents();
      setConnectionMessage({ type: 'success', text: result.message });
    } catch (err) {
      setConnectionMessage({ type: 'error', text: err.message || 'Failed to sync calendar events' });
    } finally {
      setSyncingAll(false);
    }
  }

  async function handleSyncTeam() {
    setSyncingTeam(true);
    setConnectionMessage(null);
    try {
      const result = await syncTeamCalendarEvents();
      setConnectionMessage({ type: 'success', text: result.message });
    } catch (err) {
      setConnectionMessage({ type: 'error', text: err.message || 'Failed to sync team calendar events' });
    } finally {
      setSyncingTeam(false);
    }
  }

  async function handleToggleTeamSync(provider, currentValue) {
    setTogglingTeamSync(true);
    setConnectionMessage(null);
    try {
      const result = await setTeamSyncPreference(provider, !currentValue);
      // Update local state to reflect the change
      setCalendarConnections(prev =>
        prev.map(c => c.provider === provider ? { ...c, syncTeamLeave: !currentValue } : c)
      );
      setConnectionMessage({
        type: 'success',
        text: result.syncTeamLeave
          ? 'Team leave will now auto-sync to your calendar'
          : 'Team leave auto-sync disabled'
      });
    } catch (err) {
      setConnectionMessage({ type: 'error', text: err.message || 'Failed to update team sync preference' });
    } finally {
      setTogglingTeamSync(false);
    }
  }

  async function loadData() {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRange();

      const [calData, usersData, deptsData, locsData] = await Promise.all([
        getCalendarData(startDate, endDate),
        getUsers(),
        getDepartments(),
        getLocations()
      ]);

      setCalendarData(calData);
      setUsers(usersData || []);
      setDepartments(deptsData || []);
      setLocations(locsData || []);
    } catch (error) {
      console.error('Failed to load calendar:', error);
    } finally {
      setLoading(false);
    }
  }

  function getDateRange() {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const date = currentDate.getDate();

    if (viewType === 'day') {
      const dateStr = formatDateLocal(currentDate);
      return { startDate: dateStr, endDate: dateStr };
    }

    if (viewType === 'week') {
      const day = currentDate.getDay();
      const diff = date - day + (day === 0 ? -6 : 1);
      const monday = new Date(currentDate);
      monday.setDate(diff);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return {
        startDate: formatDateLocal(monday),
        endDate: formatDateLocal(sunday)
      };
    }

    // Month view
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    return {
      startDate: formatDateLocal(firstDay),
      endDate: formatDateLocal(lastDay)
    };
  }

  function navigate(direction) {
    const newDate = new Date(currentDate);
    if (viewType === 'day') {
      newDate.setDate(newDate.getDate() + direction);
    } else if (viewType === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else {
      // For month navigation, set to 1st of month first to avoid overflow
      // (e.g., Jan 31 + 1 month would skip to Mar 2/3 because Feb doesn't have 31 days)
      newDate.setDate(1);
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setCurrentDate(newDate);
  }

  function goToToday() {
    setCurrentDate(new Date());
  }

  function getLeaveColorDefault(leaveType, leaveColor) {
    // Use the color from the database if provided
    if (leaveColor) return leaveColor;

    // Fallback mapping for common leave types
    const colors = {
      'Vacation': '#3b82f6',
      'Vacation Leave': '#3b82f6',
      'Annual Leave': '#3b82f6',
      'Sick': '#EF4444',
      'Sick Leave': '#EF4444',
      'Sick Leave (Full Pay)': '#EF4444',
      'Sick Leave (Half Pay)': '#f97316',
      'Sick day': '#EF4444',
      'Maternity': '#EC4899',
      'Maternity Leave': '#EC4899',
      'Paternity': '#8B5CF6',
      'Paternity Leave': '#8B5CF6',
      'Personal': '#10B981',
      'Bereavement': '#6B7280',
      'Bereavement Leave': '#6B7280'
    };
    return colors[leaveType] || '#EC4899'; // Default pink for unknown
  }

  // Privacy-aware wrapper - hides paid/unpaid distinction from non-admins
  function getLeaveColor(leaveType, leaveColor, leaveUserId) {
    const actualColor = getLeaveColorDefault(leaveType, leaveColor);
    const isOwnLeave = leaveUserId === user?.id;
    return getPrivacyAwareLeaveColor(leaveType, actualColor, user?.role, isOwnLeave);
  }

  // Privacy-aware leave type name
  function getLeaveTypeName(leaveTypeName, leaveUserId) {
    const isOwnLeave = leaveUserId === user?.id;
    return getPrivacyAwareLeaveTypeName(leaveTypeName, user?.role, isOwnLeave);
  }

  // Filter leaves
  const filteredLeaves = (calendarData.leaves || []).filter(leave => {
    if (departmentFilter !== 'all') {
      const leaveUser = users.find(u => u.id === leave.user_id);
      if (leaveUser?.department_id !== departmentFilter) return false;
    }
    if (locationFilter !== 'all') {
      const leaveUser = users.find(u => u.id === leave.user_id);
      if (leaveUser?.location_id !== locationFilter) return false;
    }
    if (leaveTypeFilter !== 'all' && leave.leave_type !== leaveTypeFilter) return false;
    if (statusFilter !== 'all' && leave.status !== statusFilter) return false;
    return true;
  });

  // Get unique leave types
  const leaveTypes = [...new Set((calendarData.leaves || []).map(l => l.leave_type))];

  const getViewLabel = () => {
    if (viewType === 'day') {
      return currentDate.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    }
    if (viewType === 'week') {
      const { startDate, endDate } = getDateRange();
      const start = new Date(startDate);
      const end = new Date(endDate);
      return `${start.toLocaleDateString(locale, { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
    return currentDate.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  };

  return (
    <div class="calendar-page">
      {/* Header */}
      <div class="bento-page-header-row">
        <div class="bento-page-header">
          <h1 class="bento-page-title">{t('calendar.page_title')}</h1>
          <p class="bento-page-subtitle">{t('calendar.page_subtitle')}</p>
        </div>
        <div class="calendar-actions">
          <button
            class={`btn btn-sm ${showFilters ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            {t('calendar.filters.button')}
          </button>
        </div>
      </div>

      {/* Calendar Sync Card - only shown if admin has enabled calendar integration */}
      {calendarIntegrationEnabled && (
      <div class="bento-card" style={{ marginBottom: 'var(--bento-gap)' }}>
        <div style={{ padding: '1rem 1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '0.95rem', fontWeight: 600, color: 'var(--gray-900)' }}>
                {t('calendar.sync.title')}
              </h4>
              <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--gray-500)' }}>
                {t('calendar.sync.description')}
              </p>
            </div>
            <div style="display: flex; gap: 0.75rem; flex-wrap: wrap;">
              {/* Google Calendar */}
              {calendarConnections.find(c => c.provider === 'google_calendar') ? (
                <div class="connected-calendar">
                  <svg width="20" height="20" viewBox="0 0 24 24" style="flex-shrink: 0;">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span style="font-size: 0.85rem; color: var(--gray-700);">
                    {calendarConnections.find(c => c.provider === 'google_calendar')?.email || 'Connected'}
                  </span>
                  <button
                    class="btn btn-sm"
                    style="padding: 0.25rem 0.5rem; font-size: 0.75rem; background: var(--gray-100); border: none;"
                    onClick={() => handleDisconnectCalendar('google_calendar')}
                  >
                    {t('calendar.action.disconnect')}
                  </button>
                </div>
              ) : (
                <button
                  class="btn btn-sm calendar-connect-btn google"
                  onClick={() => handleConnectCalendar('google_calendar')}
                  disabled={connectingProvider === 'google_calendar'}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {connectingProvider === 'google_calendar' ? t('calendar.connecting') : t('calendar.connect.google')}
                </button>
              )}

              {/* Microsoft Calendar */}
              {calendarConnections.find(c => c.provider === 'outlook') ? (
                <div class="connected-calendar">
                  <svg width="20" height="20" viewBox="0 0 24 24" style="flex-shrink: 0;">
                    <path fill="#00A4EF" d="M11.5 2.5v9h9v-9h-9zM2.5 2.5v9h9v-9h-9zM11.5 11.5v9h9v-9h-9zM2.5 11.5v9h9v-9h-9z"/>
                  </svg>
                  <span style="font-size: 0.85rem; color: var(--gray-700);">
                    {calendarConnections.find(c => c.provider === 'outlook')?.email || 'Connected'}
                  </span>
                  <button
                    class="btn btn-sm"
                    style="padding: 0.25rem 0.5rem; font-size: 0.75rem; background: var(--gray-100); border: none;"
                    onClick={() => handleDisconnectCalendar('outlook')}
                  >
                    {t('calendar.action.disconnect')}
                  </button>
                </div>
              ) : (
                <button
                  class="btn btn-sm calendar-connect-btn microsoft"
                  onClick={() => handleConnectCalendar('outlook')}
                  disabled={connectingProvider === 'outlook'}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path fill="#00A4EF" d="M11.5 2.5v9h9v-9h-9zM2.5 2.5v9h9v-9h-9zM11.5 11.5v9h9v-9h-9zM2.5 11.5v9h9v-9h-9z"/>
                  </svg>
                  {connectingProvider === 'outlook' ? t('calendar.connecting') : t('calendar.connect.microsoft')}
                </button>
              )}

              {/* Sync buttons - only shown when at least one calendar is connected */}
              {calendarConnections.length > 0 && (
                <>
                  <button
                    class="btn btn-sm"
                    onClick={handleSyncAll}
                    disabled={syncingAll || syncingTeam}
                    style="padding: 0.4rem 0.75rem; font-size: 0.8rem; background: var(--gray-100); border: 1px solid var(--gray-300);"
                    title="Sync only your approved leave to your calendar"
                  >
                    {syncingAll ? t('calendar.syncing') : t('calendar.action.sync_my_leave')}
                  </button>
                  <button
                    class="btn btn-sm btn-primary"
                    onClick={handleSyncTeam}
                    disabled={syncingAll || syncingTeam}
                    style="padding: 0.4rem 0.75rem; font-size: 0.8rem;"
                    title="Sync entire team's approved leave to your calendar"
                  >
                    {syncingTeam ? t('calendar.syncing') : t('calendar.action.sync_team_leave')}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Team sync auto-sync toggle - shown when calendars are connected */}
          {calendarConnections.length > 0 && (
            <div style="margin-top: 0.75rem; padding-top: 0.75rem; border-top: 1px solid var(--gray-200);">
              <div style="display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 0.5rem;">
                <div>
                  <span style="font-size: 0.85rem; font-weight: 500; color: var(--gray-700);">
                    {t('calendar.autosync.title')}
                  </span>
                  <p style="margin: 0; font-size: 0.75rem; color: var(--gray-500);">
                    {t('calendar.autosync.description')}
                  </p>
                </div>
                <div style="display: flex; gap: 1rem; align-items: center;">
                  {calendarConnections.map(conn => (
                    <label key={conn.provider} class="team-sync-toggle" style="display: flex; align-items: center; gap: 0.5rem; cursor: pointer;">
                      <span style="font-size: 0.75rem; color: var(--gray-600);">
                        {conn.provider === 'google_calendar' ? 'Google' : 'Microsoft'}
                      </span>
                      <div class="toggle-switch">
                        <input
                          type="checkbox"
                          checked={conn.syncTeamLeave}
                          disabled={togglingTeamSync}
                          onChange={() => handleToggleTeamSync(conn.provider, conn.syncTeamLeave)}
                        />
                        <span class="toggle-slider"></span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Connection message */}
          {connectionMessage && (
            <div
              class={`alert ${connectionMessage.type === 'success' ? 'alert-success' : 'alert-danger'}`}
              style="margin-top: 0.75rem; margin-bottom: 0; padding: 0.5rem 0.75rem; font-size: 0.85rem;"
            >
              {connectionMessage.text}
              <button
                style="float: right; background: none; border: none; cursor: pointer; padding: 0; font-size: 1rem;"
                onClick={() => setConnectionMessage(null)}
              >
                &times;
              </button>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <div class="bento-card bento-filters-card" style={{ marginBottom: 'var(--bento-gap)' }}>
          <div style={{ padding: '1rem' }}>
            <div class="filters-grid">
              <div class="filter-group">
                <label class="filter-label">{t('calendar.filter.department_label')}</label>
                <select
                  class="form-select form-select-sm"
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                >
                  <option value="all">{t('calendar.filter.all_departments')}</option>
                  {departments.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>

              <div class="filter-group">
                <label class="filter-label">{t('calendar.filter.location_label')}</label>
                <select
                  class="form-select form-select-sm"
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                >
                  <option value="all">{t('calendar.filter.all_locations')}</option>
                  {locations.map(l => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>

              <div class="filter-group">
                <label class="filter-label">{t('calendar.filter.leave_type_label')}</label>
                <select
                  class="form-select form-select-sm"
                  value={leaveTypeFilter}
                  onChange={(e) => setLeaveTypeFilter(e.target.value)}
                >
                  <option value="all">{t('calendar.filter.all_types')}</option>
                  {leaveTypes.map(lt => (
                    <option key={lt} value={lt}>{lt}</option>
                  ))}
                </select>
              </div>

              <div class="filter-group">
                <label class="filter-label">{t('calendar.filter.status_label')}</label>
                <select
                  class="form-select form-select-sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">{t('calendar.filter.all_status')}</option>
                  <option value="approved">{t('status.approved')}</option>
                  <option value="pending">{t('status.pending')}</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      <div class="bento-card">
        <div style={{ padding: '1.25rem' }}>
          {/* Calendar Navigation */}
          <div class="calendar-header">
            <div class="calendar-nav">
              <button class="btn btn-sm btn-secondary" onClick={() => navigate(-1)}>
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button class="btn btn-sm btn-secondary" onClick={goToToday}>
                {t('calendar.nav.today')}
              </button>
              <button class="btn btn-sm btn-secondary" onClick={() => navigate(1)}>
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            <h3 class="calendar-title">{getViewLabel()}</h3>

            <div class="view-toggle">
              <button
                class={`toggle-btn ${viewType === 'day' ? 'active' : ''}`}
                onClick={() => setViewType('day')}
              >
                {t('calendar.view.day')}
              </button>
              <button
                class={`toggle-btn ${viewType === 'week' ? 'active' : ''}`}
                onClick={() => setViewType('week')}
              >
                {t('calendar.view.week')}
              </button>
              <button
                class={`toggle-btn ${viewType === 'month' ? 'active' : ''}`}
                onClick={() => setViewType('month')}
              >
                {t('calendar.view.month')}
              </button>
            </div>
          </div>

          {loading ? (
            <div class="loading-screen" style="min-height: 400px;">
              <div class="spinner" />
            </div>
          ) : viewType === 'day' ? (
            <DayView
              date={currentDate}
              leaves={filteredLeaves}
              holidays={calendarData.holidays}
              getLeaveColor={getLeaveColor}
              getLeaveTypeName={getLeaveTypeName}
              t={t}
              locale={locale}
            />
          ) : viewType === 'week' ? (
            <div class="calendar-scroll-container">
              <WeekView
                date={currentDate}
                leaves={filteredLeaves}
                holidays={calendarData.holidays}
                getLeaveColor={getLeaveColor}
                getLeaveTypeName={getLeaveTypeName}
                onShowMore={setSelectedDay}
                t={t}
                locale={locale}
              />
            </div>
          ) : (
            <div class="calendar-scroll-container">
              <MonthView
                date={currentDate}
                leaves={filteredLeaves}
                holidays={calendarData.holidays}
                getLeaveColor={getLeaveColor}
                getLeaveTypeName={getLeaveTypeName}
                onShowMore={setSelectedDay}
                t={t}
                locale={locale}
              />
            </div>
          )}
        </div>
      </div>

      {/* Legend - dynamically show all leave types with their colors (privacy-aware) */}
      <div class="bento-card" style={{ marginTop: 'var(--bento-gap)', padding: '0.75rem 1rem' }}>
        <div class="calendar-legend-bar" style={{ margin: 0, padding: 0 }}>
        {/* Get unique leave types with privacy-aware names/colors - groups Paid/Unpaid into generic categories for non-admins */}
        {[...new Map((calendarData.leaves || []).map(l => {
          const privacyName = getLeaveTypeName(l.leave_type, l.user_id);
          const privacyColor = getLeaveColor(l.leave_type, l.color, l.user_id);
          return [privacyName, { name: privacyName, color: privacyColor }];
        })).values()].map(lt => (
          <div class="legend-item" key={lt.name}>
            <span class="legend-dot" style={{ backgroundColor: lt.color }} />
            <span>{lt.name}</span>
          </div>
        ))}
        <div class="legend-item">
          <span class="legend-dot pending-stripe" />
          <span>{t('calendar.legend.pending')}</span>
        </div>
        <div class="legend-item">
          <span class="legend-dot holiday" />
          <span>{t('calendar.legend.holiday')}</span>
        </div>
        </div>
      </div>

      {/* Day Detail Modal */}
      {selectedDay && (
        <div class="modal-overlay" onClick={() => setSelectedDay(null)}>
          <div class="modal-content day-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div class="modal-header">
              <h2>{new Date(selectedDay.dateStr).toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</h2>
              <button class="modal-close" onClick={() => setSelectedDay(null)}>&times;</button>
            </div>
            <div class="modal-body">
              {selectedDay.holidays && selectedDay.holidays.length > 0 && (
                <div class="day-detail-holidays">
                  {selectedDay.holidays.map((h, i) => (
                    <div key={i} class="holiday-banner" style={{ backgroundColor: h.color || '#10B981' }}>
                      🎉 {h.name}
                    </div>
                  ))}
                </div>
              )}
              {selectedDay.events && selectedDay.events.length > 0 ? (
                <div class="day-detail-leaves">
                  {selectedDay.events.map((event, i) => (
                    <div key={i} class="day-detail-leave-item">
                      <div class="avatar">
                        {event.avatar ? <img src={event.avatar} alt="" /> : event.userName?.charAt(0)}
                      </div>
                      <div class="leave-details">
                        <div class="user-name">{event.userName}</div>
                        <div class="leave-type-badge" style={{ backgroundColor: event.color }}>
                          {event.type}
                        </div>
                        <div class="leave-dates">
                          {event.startDate === event.endDate
                            ? new Date(event.startDate + 'T12:00:00').toLocaleDateString(locale, { day: 'numeric', month: 'short' })
                            : `${new Date(event.startDate + 'T12:00:00').toLocaleDateString(locale, { day: 'numeric', month: 'short' })} - ${new Date(event.endDate + 'T12:00:00').toLocaleDateString(locale, { day: 'numeric', month: 'short' })}`
                          }
                        </div>
                      </div>
                      <div class={`leave-status ${event.status}`}>
                        {t(`status.${event.status}`)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div class="empty-state">
                  <p>{t('calendar.day_view.empty')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        /* Calendar Connect Buttons */
        .calendar-connect-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border: 1px solid #e5e7eb;
          background: white;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .calendar-connect-btn:hover {
          background: #f9fafb;
          border-color: #d1d5db;
        }

        .calendar-connect-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .connected-calendar {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem;
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          border-radius: 6px;
        }

        .alert-success {
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          color: #166534;
          border-radius: 6px;
        }

        .alert-danger {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #991b1b;
          border-radius: 6px;
        }

        /* Toggle Switch */
        .toggle-switch {
          position: relative;
          width: 36px;
          height: 20px;
        }

        .toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #d1d5db;
          transition: 0.2s;
          border-radius: 20px;
        }

        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 14px;
          width: 14px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: 0.2s;
          border-radius: 50%;
        }

        .toggle-switch input:checked + .toggle-slider {
          background-color: #3b82f6;
        }

        .toggle-switch input:disabled + .toggle-slider {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .toggle-switch input:checked + .toggle-slider:before {
          transform: translateX(16px);
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .day-detail-modal {
          background: white;
          border-radius: 12px;
          width: 90%;
          max-width: 500px;
          max-height: 80vh;
          overflow-y: auto;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 1.125rem;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #6b7280;
        }

        .modal-body {
          padding: 1rem 1.5rem;
        }

        .day-detail-holidays {
          margin-bottom: 1rem;
        }

        .day-detail-leaves {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .day-detail-leave-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem;
          background: #f9fafb;
          border-radius: 8px;
        }

        .day-detail-leave-item .avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #e5e7eb;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          color: #4b5563;
          overflow: hidden;
        }

        .day-detail-leave-item .avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .leave-details {
          flex: 1;
        }

        .leave-details .user-name {
          font-weight: 500;
          margin-bottom: 0.25rem;
        }

        .leave-details .leave-type-badge {
          display: inline-block;
          padding: 0.125rem 0.5rem;
          border-radius: 4px;
          font-size: 0.75rem;
          color: white;
          margin-bottom: 0.25rem;
        }

        .leave-details .leave-dates {
          font-size: 0.75rem;
          color: #6b7280;
        }

        .leave-status {
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          text-transform: capitalize;
        }

        .leave-status.approved {
          background: #d1fae5;
          color: #065f46;
        }

        .leave-status.pending {
          background: #fef3c7;
          color: #92400e;
        }

        .calendar-event.more {
          cursor: pointer;
        }

        .calendar-event.more:hover {
          opacity: 0.8;
        }

        .week-event.more {
          cursor: pointer;
        }

        .week-event.more:hover {
          opacity: 0.8;
        }
      `}</style>
    </div>
  );
}

function formatDateShort(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

// Format date as YYYY-MM-DD in LOCAL time (toISOString() is UTC and shifts
// the date by one day for users behind UTC, e.g. the Americas)
function formatDateLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function DayView({ date, leaves, holidays, getLeaveColor, getLeaveTypeName, t, locale }) {
  const dateStr = formatDateLocal(date);
  const dayLeaves = leaves.filter(l => dateStr >= l.start_date && dateStr <= l.end_date);
  const dayHolidays = holidays.filter(h => h.date === dateStr);

  const formatDate = (d) => new Date(d + 'T12:00:00').toLocaleDateString(locale, { day: 'numeric', month: 'short' });

  return (
    <div class="day-view">
      {dayHolidays.length > 0 && (
        <div class="day-holidays">
          {dayHolidays.map(h => (
            <div key={h.id} class="holiday-banner" style={{ backgroundColor: h.color || '#10B981', color: 'white' }}>
              🎉 {h.name}
            </div>
          ))}
        </div>
      )}

      {dayLeaves.length > 0 ? (
        <div class="day-leaves-list">
          {dayLeaves.map(leave => (
            <div
              key={leave.id}
              class={`day-leave-item ${leave.status}`}
              style={{ borderLeftColor: getLeaveColor(leave.leave_type, leave.color, leave.user_id) }}
            >
              <div class="leave-user-info">
                <div class="avatar">
                  {leave.avatar_url ? <img src={leave.avatar_url} alt="" /> : leave.user_name?.charAt(0)}
                </div>
                <div>
                  <div class="user-name">{leave.user_name}</div>
                  <div class="leave-type" style={{ color: getLeaveColor(leave.leave_type, leave.color, leave.user_id) }}>
                    {leave.icon} {getLeaveTypeName(leave.leave_type, leave.user_id)}
                  </div>
                </div>
              </div>
              <div class="leave-dates">
                {leave.start_date === leave.end_date ? (
                  formatDate(leave.start_date)
                ) : (
                  `${formatDate(leave.start_date)} - ${formatDate(leave.end_date)}`
                )}
              </div>
              <div class={`leave-status badge badge-${leave.status}`}>
                {t(`status.${leave.status}`)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div class="empty-state" style="padding: 3rem;">
          <div class="empty-state-icon">📅</div>
          <h3>{t('calendar.day_view.empty')}</h3>
        </div>
      )}
    </div>
  );
}

function WeekView({ date, leaves, holidays, getLeaveColor, getLeaveTypeName, onShowMore, t, locale }) {
  const weekDays = getWeekDays(date);
  const today = formatDateLocal(new Date());

  return (
    <div class="week-view">
      <div class="week-grid">
        {weekDays.map(day => {
          const dateStr = formatDateLocal(day);
          const dayLeaves = leaves.filter(l => dateStr >= l.start_date && dateStr <= l.end_date);
          const dayHolidays = holidays.filter(h => h.date === dateStr);
          const isToday = dateStr === today;
          const isWeekend = [0, 6].includes(day.getDay());

          const dayEvents = dayLeaves.map(l => ({
            userName: l.user_name,
            type: getLeaveTypeName(l.leave_type, l.user_id),
            color: getLeaveColor(l.leave_type, l.color, l.user_id),
            status: l.status,
            startDate: l.start_date,
            endDate: l.end_date,
            avatar: l.avatar_url
          }));

          return (
            <div key={dateStr} class={`week-day ${isToday ? 'today' : ''} ${isWeekend ? 'weekend' : ''}`}>
              <div class="week-day-header">
                <span class="week-day-name">{day.toLocaleDateString(locale, { weekday: 'short' })}</span>
                <span class="week-day-date">{day.getDate()}</span>
              </div>
              <div class="week-day-content">
                {dayHolidays.map(h => (
                  <div key={h.id} class="week-event" style={{ backgroundColor: h.color || '#10B981', color: 'white' }}>
                    🎉 {h.name}
                  </div>
                ))}
                {dayLeaves.slice(0, 4).map(leave => (
                  <div
                    key={leave.id}
                    class={`week-event leave ${leave.status}`}
                    style={{ backgroundColor: getLeaveColor(leave.leave_type, leave.color, leave.user_id) }}
                    title={`${leave.user_name} - ${getLeaveTypeName(leave.leave_type, leave.user_id)}`}
                  >
                    {leave.user_name?.split(' ')[0]}
                  </div>
                ))}
                {dayLeaves.length > 4 && (
                  <div
                    class="week-event more"
                    onClick={() => onShowMore({ dateStr, events: dayEvents, holidays: dayHolidays })}
                  >
                    {t('calendar.more', { count: dayLeaves.length - 4 })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MonthView({ date, leaves, holidays, getLeaveColor, getLeaveTypeName, onShowMore, t, locale }) {
  const calendarDays = generateCalendarDays(date, leaves, holidays, getLeaveColor, getLeaveTypeName);

  // Generate localized day names starting from Monday
  const dayNames = [];
  const refDate = new Date(2024, 0, 1); // January 1, 2024 is a Monday
  for (let i = 0; i < 7; i++) {
    const d = new Date(refDate);
    d.setDate(refDate.getDate() + i);
    dayNames.push(d.toLocaleDateString(locale, { weekday: 'short' }));
  }

  return (
    <div class="calendar-grid">
      {dayNames.map(day => (
        <div key={day} class="calendar-day-header">{day}</div>
      ))}

      {calendarDays.map((day, index) => (
        <div
          key={index}
          class={`calendar-day ${day.isOtherMonth ? 'other-month' : ''} ${day.isToday ? 'today' : ''} ${day.isWeekend ? 'weekend' : ''}`}
        >
          <div class="calendar-date">{day.date}</div>
          {day.events.slice(0, 3).map((event, i) => (
            <div
              key={i}
              class={`calendar-event ${event.status}`}
              style={{ backgroundColor: event.color, color: 'white' }}
              title={`${event.userName} - ${event.type}`}
            >
              {event.userName}
            </div>
          ))}
          {day.events.length > 3 && (
            <div
              class="calendar-event more"
              onClick={() => onShowMore({ dateStr: day.dateStr, events: day.events, holidays: day.holidays })}
            >
              {t('calendar.more', { count: day.events.length - 3 })}
            </div>
          )}
          {day.holidays.map((holiday, i) => (
            <div
              key={i}
              class="calendar-event"
              style={{ backgroundColor: holiday.color || '#10B981', color: 'white' }}
              title={holiday.name}
            >
              🎉 {holiday.name.length > 10 ? holiday.name.slice(0, 10) + '...' : holiday.name}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function getWeekDays(date) {
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(date);
  monday.setDate(diff);

  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    days.push(d);
  }
  return days;
}

function generateCalendarDays(currentDate, leaves, holidays, getLeaveColor, getLeaveTypeName) {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  let startDayOfWeek = firstDay.getDay();
  startDayOfWeek = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;

  const days = [];
  const today = formatDateLocal(new Date());

  // Add days from previous month
  const prevMonthLastDay = new Date(year, month, 0).getDate();
  for (let i = startDayOfWeek - 1; i >= 0; i--) {
    const d = prevMonthLastDay - i;
    const prevMonth = month === 0 ? 12 : month;
    const prevYear = month === 0 ? year - 1 : year;
    const dateStr = `${prevYear}-${String(prevMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const dayOfWeek = new Date(dateStr).getDay();

    days.push({
      date: d,
      dateStr,
      isOtherMonth: true,
      isToday: false,
      isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
      events: [],
      holidays: []
    });
  }

  // Add days of current month
  for (let d = 1; d <= lastDay.getDate(); d++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const dayOfWeek = new Date(dateStr).getDay();

    const events = (leaves || [])
      .filter(leave => dateStr >= leave.start_date && dateStr <= leave.end_date)
      .map(leave => ({
        id: leave.id,
        userName: leave.user_name,
        type: getLeaveTypeName(leave.leave_type, leave.user_id),
        icon: leave.icon || '📅',
        color: getLeaveColor(leave.leave_type, leave.color, leave.user_id),
        status: leave.status,
        startDate: leave.start_date,
        endDate: leave.end_date,
        avatar: leave.avatar_url
      }));

    const dayHolidays = (holidays || []).filter(h => h.date === dateStr);

    days.push({
      date: d,
      dateStr,
      isOtherMonth: false,
      isToday: dateStr === today,
      isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
      events,
      holidays: dayHolidays
    });
  }

  // Add days from next month
  const remainingDays = 42 - days.length;
  for (let d = 1; d <= remainingDays; d++) {
    const nextMonth = month === 11 ? 1 : month + 2;
    const nextYear = month === 11 ? year + 1 : year;
    const dateStr = `${nextYear}-${String(nextMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const dayOfWeek = new Date(dateStr).getDay();

    days.push({
      date: d,
      dateStr,
      isOtherMonth: true,
      isToday: false,
      isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
      events: [],
      holidays: []
    });
  }

  return days;
}
